import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  ButtonBase,
  ClickAwayListener,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Skeleton,
  Stack,
  Switch,
  Typography,
  useMediaQuery
} from '@mui/material';

// project import
import Transitions from 'components/@extended/Transitions';
import useAuth from 'hooks/useAuth';
import useConfig from 'hooks/useConfig';
import Avatar from 'components/@extended/Avatar';
import Chip from 'components/ui/Chip';
import { useBillingClient } from 'client/client-hook';
import { BillingOverviewReply, ListSubscriptionsReply } from 'proto/billing-manager_pb';

// assets
import { Crisp } from 'crisp-sdk-web';
import {
  BulbOutlined,
  CustomerServiceOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  UserOutlined
} from '@ant-design/icons';
import chevronIcon from 'assets/images/icons/chevron.svg';
import { sizeByBreakpoint, textSizeByBreakpoint } from '../../../../../utils/helper';
import { CustomListItemProps } from '../../../../../types/layout';

// types
// interface TabPanelProps {
//   children?: ReactNode;
//   dir?: string;
//   index: number;
//   value: number;
// }

// tab panel wrapper
// function TabPanel(props: TabPanelProps) {
//   const { children, value, index, ...other } = props;

//   return (
//     <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
//       {value === index && children}
//     </div>
//   );
// }

// function a11yProps(index: number) {
//   return {
//     id: `profile-tab-${index}`,
//     'aria-controls': `profile-tabpanel-${index}`
//   };
// }

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

const Profile = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { mode, onChangeMode } = useConfig();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { logout, user } = useAuth();
  const getBillingClient = useBillingClient();
  const [billingOverview, setBillingOverview] = useState<BillingOverviewReply.AsObject | null>(null);

  useEffect(() => {
    if (user?.tcId) {
      getBillingClient().then(async (client) => {
        try {
          const [overviewRes, subscriptionsRes] = await Promise.all([
            client.billingOverview({ userId: user.tcId || '' }),
            client.listSubscriptions({ userId: user.tcId || '', statusList: ['active'] })
          ]);
          
          const overview = overviewRes as BillingOverviewReply.AsObject;
          const subscriptionList = (subscriptionsRes as ListSubscriptionsReply.AsObject).subscriptionsList || [];
          
          // Compute active subscription name from subscriptions list and user roles
          // Check for premium-plan role to determine if user has premium
          const hasPremiumRole = user?.roles?.includes('premium-plan') || false;
          
          const copierSubscriptions = subscriptionList.filter((s) => {
            const productName = s.productName?.toLowerCase() || '';
            return productName.includes('account') || productName.includes('copier') || 
                   productName.includes('standard') || productName.includes('premium');
          });
          const hasAnalyzer = subscriptionList.some((s) => s.productName?.toLowerCase().includes('analyzer'));
          const hasDedicatedEnv = subscriptionList.some((s) => s.productName?.toLowerCase().includes('dedicated'));
          const hasCopierSubscription = copierSubscriptions.length > 0;

          let computedActiveSubscription = '';
          if (hasPremiumRole && hasCopierSubscription) {
            computedActiveSubscription = 'Premium';
          } else if (hasCopierSubscription) {
            computedActiveSubscription = 'Standard';
          } else if (hasAnalyzer) {
            computedActiveSubscription = 'Analyzer';
          } else if (hasDedicatedEnv) {
            computedActiveSubscription = 'Dedicated Environment';
          }
          
          // Always use role-based computed value for activeSubscription
          setBillingOverview({
            ...overview,
            activeSubscription: computedActiveSubscription || overview.activeSubscription
          });
        } catch (error) {
          console.error('Failed to fetch billing overview:', error);
        }
      });
    }
  }, [user, getBillingClient]);

  const handleLogout = async () => {
    try {
      Crisp.session.reset();
      await logout();
    } catch (err) {
      console.error(err);
    }
  };

  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleModeChange = () => {
    const newMode = mode === 'dark' ? 'light' : 'dark';
    onChangeMode(newMode);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  // const [value, setValue] = useState(0);

  // const handleChange = (event: SyntheticEvent, newValue: number) => {
  //   setValue(newValue);
  // };

  const CustomListItem: React.FC<CustomListItemProps> = ({
    Icon,
    primaryText,
    secondaryText,
    children,
    onClick,
    sx,
    hoverEffect = true
  }) => {
    return (
      <ListItem
        component="div"
        sx={{
          cursor: hoverEffect ? 'pointer' : 'normal',
          padding: 0,
          '&:hover': {
            backgroundColor: 'transparent',
            ...(hoverEffect
              ? {
                  '& .MuiListItemIcon-root': {
                    color: theme.palette.primary.main,
                    transition: 'color 100ms ease-in-out'
                  },
                  '& .MuiListItemText-root': {
                    color: theme.palette.primary.main,
                    transition: 'color 100ms ease-in-out'
                  }
                }
              : {})
          },
          ...sx
        }}
        onClick={onClick}
      >
        <ListItemIcon sx={{ minWidth: sizeByBreakpoint(1.5, 32) }}>
          <Box
            component={Icon}
            sx={{
              fontSize: textSizeByBreakpoint(18)
            }}
          />
        </ListItemIcon>
        <ListItemText
          primary={primaryText}
          secondary={secondaryText}
          primaryTypographyProps={{
            fontSize: textSizeByBreakpoint(14),
            fontWeight: 500
          }}
          sx={{
            margin: 0
          }}
        />
        {children}
      </ListItem>
    );
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        sx={{
          p: isMobile ? 0 : 0.25,
          bgcolor: 'transparent',
          borderRadius: 1,
          cursor: 'pointer',
          justifyContent: 'flex-start',
          '&:hover': { bgcolor: 'transparent' },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.secondary.dark}`,
            outlineOffset: 2
          }
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        {isMobile ? (
          <Avatar alt="profile user" src={user?.picture} size="sm" />
        ) : (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 0.25 }}>
            <Avatar alt="profile user" src={user?.picture} size="sm" />
            <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'flex-start' }}>
              <Typography variant="subtitle1" sx={{ fontSize: '0.875rem', fontWeight: 700, lineHeight: 1.2 }}>
                {user?.name}
              </Typography>
              {billingOverview ? (
                <Chip
                  label={billingOverview.activeSubscription || 'Free'}
                  size="small"
                  fontWeight={600}
                  customColor={mode === 'dark' ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.dark, 0.1)}
                  customTextColor={mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark}
                  sx={{ cursor: 'pointer', fontSize: '0.7rem', height: '18px' }}
                />
              ) : (
                <Skeleton variant="rounded" width={60} height={18} />
              )}
            </Box>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
              <img
                src={chevronIcon}
                alt="chevron"
                style={{
                  width: 10,
                  height: 10,
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}
              />
            </Box>
          </Stack>
        )}
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            {open && (
              <Paper
                sx={{
                  boxShadow: theme.customShadows.headerDropdown,
                  marginTop: 0.5,
                  width: 280,
                  minWidth: 280,
                  maxWidth: 280,
                  maxHeight: 'calc(100vh - 100px)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  [theme.breakpoints.down('md')]: {
                    width: 260,
                    minWidth: 260,
                    maxWidth: 260
                  }
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: 'auto',
                    padding: sizeByBreakpoint(0.75, 14),
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }}
                >
                  <ClickAwayListener onClickAway={handleClose}>
                    <Stack direction="column" gap={sizeByBreakpoint(0.75, 14)} divider={<Divider />}>
                      <Stack direction="column" gap={sizeByBreakpoint(0.75, 10)}>
                        {/* Header Section */}
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Avatar alt="profile user" src={user?.picture} size="sm" />
                          <Box
                            sx={{
                              textAlign: 'left',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.25,
                              alignItems: 'flex-start'
                            }}
                          >
                            <Typography variant="subtitle1" sx={{ fontSize: '0.875rem', fontWeight: 700, lineHeight: 1.2 }}>
                              {user?.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              {user?.email}
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>

                      {/* Dark Mode */}
                      <CustomListItem Icon={BulbOutlined} primaryText="Dark Mode" hoverEffect={false}>
                        <Switch size="small" color="primary" checked={mode === 'dark'} onChange={handleModeChange} sx={{ margin: 0 }} />
                      </CustomListItem>

                      <Stack direction="column" gap={sizeByBreakpoint(0.75, 14)}>
                        {/* Profile & Settings */}
                        <CustomListItem
                          Icon={UserOutlined}
                          primaryText="Profile & Settings"
                          onClick={() => handleNavigate('/account-settings')}
                        />
                        {/* Subscription & Billing */}
                        <CustomListItem
                          Icon={SettingOutlined}
                          primaryText="Subscription & Billing"
                          onClick={() => handleNavigate('/account-settings/billing')}
                        />
                      </Stack>

                      <Stack direction="column" gap={sizeByBreakpoint(0.75, 14)}>
                        {/* Guides */}
                        <CustomListItem
                          Icon={QuestionCircleOutlined}
                          primaryText="Guides"
                          onClick={() => window.open('https://docs.tradersconnect.com', '_blank')}
                        />
                        {/* Live Chat */}
                        <CustomListItem
                          Icon={CustomerServiceOutlined}
                          primaryText="Live Chat"
                          onClick={() => {
                            setOpen(false);
                            Crisp.chat.show();
                            Crisp.chat.open();
                          }}
                        />
                      </Stack>

                      <Stack direction="column" gap={sizeByBreakpoint(0.75, 14)}>
                        {/* Log out */}
                        <CustomListItem
                          Icon={LogoutOutlined}
                          primaryText="Log out"
                          onClick={handleLogout}
                          hoverEffect={false}
                          sx={{
                            '& .MuiListItemIcon-root': {
                              color: theme.palette.error.main
                            },
                            '& .MuiListItemText-root': {
                              color: theme.palette.error.main
                            },
                            cursor: 'pointer'
                          }}
                        />
                      </Stack>
                    </Stack>
                  </ClickAwayListener>
                </Box>
              </Paper>
            )}
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default Profile;
